import React from "react";
import { Box, Paper, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "components";
import { ChartDetailsPanelProps } from "./types";

export const ChartDetailsPanel: React.FC<ChartDetailsPanelProps> = ({
    isOpen,
    onClose,
    title,
    category,
    total,
    unit,
    points,
    height = 450,
}) => {
    if (!isOpen) return null;

    return (
        <Paper
            elevation={3}
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "25%",
                height,
                transition: "width 0.3s ease-in-out",
                borderRadius: 2,
                backgroundColor: "white",
                border: `1px solid ${COLORS.Mainblue}20`,
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: '12px',
                borderBottom: `1px solid ${COLORS.Mainblue}20`,
                backgroundColor: `${COLORS.Mainblue}05`,
            }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: COLORS.Mainblue,
                            fontWeight: "bold",
                            fontSize: "16px",
                        }}
                    >
                        {title} - {category}
                    </Typography>
                    {total && <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: "normal",
                            color: COLORS.darkGray,
                            fontSize: "14px",
                        }}
                    >
                        Total: {Number(total).toLocaleString('en-US')} {unit}
                    </Typography>
                    }
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: COLORS.Mainblue,
                        "&:hover": {
                            backgroundColor: `${COLORS.Mainblue}10`,
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content with scroll */}
            <Box sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}>
                <Box sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: '12px',
                    "&::-webkit-scrollbar": {
                        width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: `${COLORS.Mainblue}10`,
                        borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: COLORS.Mainblue,
                        borderRadius: "3px",
                    },
                }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {points
                            .sort((a, b) => {
                                // Keep "Total" at the bottom
                                if (a.seriesName === "Total") return 1;
                                if (b.seriesName === "Total") return -1;
                                // Sort others in descending order by value
                                return b.value - a.value;
                            })
                            .map((point) => (
                                <Box
                                    key={point.seriesName}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: '8px 10px',
                                        gap: 1,
                                        borderRadius: 1,
                                        backgroundColor: `${COLORS.Mainblue}10`,
                                        border: point.seriesName === "Total" ? `1px solid ${COLORS.Mainblue}30` : "1px solid transparent",
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, maxWidth: "60%" }}>
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                minWidth: 10,
                                                minHeight: 10,
                                                borderRadius: "50%",
                                                backgroundColor: point.color === "transparent" ? COLORS.Mainblue : point.color,
                                                border: point.color === "transparent" ? `2px solid ${COLORS.Mainblue}` : "none",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: "13px",
                                                fontWeight: point.seriesName === "Total" ? "bold" : "normal",
                                                color: point.seriesName === "Total" ? COLORS.Mainblue : "inherit",
                                            }}
                                        >
                                            {point.seriesName}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: "bold",
                                            color: point.seriesName === "Total" ? COLORS.Mainblue : "inherit",
                                        }}
                                    >
                                        {Number(point.value.toFixed(2)).toLocaleString('en-US')} {unit}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
}; 